/**
 * Document Chat Modal
 * 
 * Refactored to separate the chat content from the Dialog wrapper.
 * This allows the chat to be embedded as a tab or shown in a modal.
 */

import React from 'react';
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Paper,
  Alert,
  Button,
  Chip,
  Fade,
  CircularProgress,
  Tooltip,
  Avatar
} from '@mui/material';
import {
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  ChatBubbleOutline as ChatIcon,
  Star as PremiumIcon,
  AutoFixHigh as ResearchIcon,
  SmartToy as AssistantIcon,
  Lightbulb as SuggestionIcon,
  Search as SearchIcon,
  Article as ArticleIcon,
  CheckCircleOutline as CheckIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import { useDocumentChat, DocumentChatMessage } from '../../hooks/useDocumentChat';
import { ChatInput } from '../chat/ChatInput';
import { ResearchStatus, getChatMarkdownStyles } from '../chat/ResearchStatus';

interface DocumentChatContentProps {
  sourceJobId: string;
  documentTitle?: string;
  expiresAt?: string;
  showHeader?: boolean;
  onClose?: () => void;
  // Optional style overrides for embedding
  embedded?: boolean;
}

interface DocumentChatModalProps {
  open: boolean;
  onClose: () => void;
  sourceJobId: string;
  documentTitle?: string;
  expiresAt?: string;
}

/**
 * Simplified message list component for document chat
 */
const DocumentMessageList: React.FC<{ 
  messages: DocumentChatMessage[];
  onSuggestionClick?: (suggestion: string) => void;
}> = ({ messages, onSuggestionClick }) => {
  const messagesEndRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const suggestions = [
    { text: "Extract key facts, dates, and locations from the evidence", icon: <SearchIcon fontSize="small" /> },
    { text: "Identify all parties mentioned and their involvement", icon: <AssistantIcon fontSize="small" /> },
    { text: "List any specific claims, demands, or allegations found", icon: <ArticleIcon fontSize="small" /> },
  ];

  if (messages.length === 0) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center',
          height: '100%',
          p: 3,
          textAlign: 'center'
        }}
      >
        <Box 
          sx={{ 
            width: 80, 
            height: 80, 
            flexShrink: 0,
            borderRadius: '50%', 
            bgcolor: 'primary.lighter', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            mb: 3,
            color: 'primary.main',
            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.15)',
            border: '1px solid',
            borderColor: 'primary.light'
          }}
        >
          <AssistantIcon sx={{ fontSize: 40 }} />
        </Box>
        
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: 'text.primary' }}>
          Document Assistant
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 450, mb: 4, lineHeight: 1.6 }}>
          I've analyzed your documents and I'm ready to answer any questions about them. 
          Ask me to find specific facts, compare statements, or summarize evidence.
        </Typography>

        <Box sx={{ width: '100%', maxWidth: 850 }}>
          <Typography 
            variant="overline" 
            sx={{ 
              display: 'block', 
              mb: 2, 
              color: 'text.secondary',
              fontWeight: 700,
              letterSpacing: 1.5
            }}
          >
            Suggested Questions
          </Typography>
          <Box 
            sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, 
              gap: 1.5, 
              justifyContent: 'center'
            }}
          >
            {suggestions.map((suggestion, index) => (
              <Chip
                key={index}
                label={suggestion.text}
                icon={suggestion.icon}
                onClick={() => onSuggestionClick?.(suggestion.text)}
                clickable
                sx={{
                  py: 1.5,
                  px: 1,
                  height: 'auto',
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: 'primary.lighter',
                    borderColor: 'primary.light',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.05)'
                  },
                  '& .MuiChip-label': {
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    whiteSpace: 'normal',
                    lineHeight: 1.3,
                    textAlign: 'left'
                  },
                  '& .MuiChip-icon': {
                    ml: 1.5
                  }
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {messages.map((message) => (
        <DocumentMessageItem key={message.id} message={message} />
      ))}
      <div ref={messagesEndRef} />
    </Box>
  );
};

const DocumentMessageItem: React.FC<{ message: DocumentChatMessage }> = ({ message }) => {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        alignItems: 'flex-start',
        flexDirection: isUser ? 'row-reverse' : 'row',
        mb: 1
      }}
    >
      {/* Avatar/Icon for Assistant */}
      {isAssistant && (
        <Avatar
          sx={{
            bgcolor: message.status === 'error' ? 'error.light' : 'primary.main',
            width: 32,
            height: 32,
            mt: 0.5,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <AssistantIcon sx={{ fontSize: 20 }} />
        </Avatar>
      )}

      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          maxWidth: isAssistant && message.status === 'processing' ? '100%' : '85%',
          bgcolor: isUser ? '#223343' : '#f8fafc',
          borderRadius: 3,
          border: isUser ? 'none' : '1px solid',
          borderColor: 'divider',
          ...(isUser && { 
            color: 'white'
          }),
          ...(!isUser && {
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
          })
        }}
      >
        {/* Deep Research Loading State */}
        {isAssistant && (message.status === 'processing' || message.status === 'queued') && !message.content && (
          <ResearchStatus 
            title="Deep Research in progress"
            steps={[
              'Deep Researching your documents...',
              'Analyzing context and evidence...',
              'Cross-referencing provided information...',
              'Extracting key facts and findings...',
              'Formulating a comprehensive response...',
              'Finalizing analysis...'
            ]}
          />
        )}

        {/* Traditional loading indicator (if content is partially present or for simpler states) */}
        {isAssistant && message.status === 'processing' && message.content && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, color: 'primary.main' }}>
            <CircularProgress size={14} thickness={5} color="inherit" />
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Synthesizing response...
            </Typography>
          </Box>
        )}

        {/* Message content with Markdown */}
        {(message.content || message.status === 'error') && (
          <Box sx={getChatMarkdownStyles(isUser)}>
            {message.content ? (
              <ReactMarkdown>{message.content}</ReactMarkdown>
            ) : (
              message.status !== 'error' && (
                <Typography variant="body2" color="text.secondary">
                  Thinking...
                </Typography>
              )
            )}
          </Box>
        )}

        {/* Error message */}
        {message.status === 'error' && message.errorMessage && (
          <Alert 
            severity="error" 
            variant="outlined"
            sx={{ 
              mt: 1, 
              py: 0,
              bgcolor: 'error.lighter',
              borderColor: 'error.light'
            }}
          >
            {message.errorMessage}
          </Alert>
        )}

        {/* Timestamp & Metadata */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mt: 1.5,
          pt: 1,
          borderTop: '1px solid',
          borderColor: isUser ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'
        }}>
          <Typography 
            variant="caption" 
            sx={{ 
              opacity: 0.7,
              fontSize: '0.65rem',
              fontWeight: 500
            }}
          >
            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </Typography>
          
          {isAssistant && message.metadata && message.status === 'completed' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {/* Confidence Score */}
              {typeof message.metadata.confidence === 'number' && (
                <Tooltip title={`AI Confidence: ${Math.round(message.metadata.confidence * 100)}%`}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box 
                      sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        bgcolor: message.metadata.confidence >= 0.8 ? 'success.main' : 
                                 message.metadata.confidence >= 0.5 ? 'warning.main' : 'error.main'
                      }} 
                    />
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600, opacity: 0.8 }}>
                      {Math.round(message.metadata.confidence * 100)}% Confidence
                    </Typography>
                  </Box>
                </Tooltip>
              )}

              {/* Verified Badge */}
              <Tooltip title={`Searched ${message.metadata.chunks_searched} document sections`}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CheckIcon sx={{ fontSize: 12, color: 'success.main' }} />
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', opacity: 0.8 }}>
                    Verified analysis
                  </Typography>
                </Box>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

/**
 * The core chat content component that can be used anywhere
 */
export const DocumentChatContent: React.FC<DocumentChatContentProps> = ({
  sourceJobId,
  documentTitle,
  expiresAt,
  showHeader = false,
  onClose,
  embedded = false
}) => {
  const {
    messages,
    isLoading,
    error,
    sendMessage,
    clearMessages,
    retryLastMessage
  } = useDocumentChat({ sourceJobId, documentTitle });

  // Calculate expiry text
  const getExpiryText = () => {
    if (!expiresAt) return null;
    const expiry = new Date(expiresAt);
    const now = new Date();
    const daysRemaining = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysRemaining <= 0) return 'Expired';
    if (daysRemaining === 1) return 'Expires tomorrow';
    if (daysRemaining <= 7) return `Expires in ${daysRemaining} days`;
    return `Expires in ${daysRemaining} days`;
  };

  const expiryText = getExpiryText();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Optional Header - matches ModalHeader style */}
      {showHeader && (
        <Box sx={{ 
          bgcolor: '#223343',
          color: '#ffffff',
          p: 3,
          position: 'relative'
        }}>
          {/* Close Button */}
          {onClose && (
            <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
              <Button
                onClick={onClose}
                sx={{
                  minWidth: 'auto',
                  p: 0.5,
                  borderRadius: 1,
                  color: 'rgba(255, 255, 255, 0.8)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: '#ffffff'
                  }
                }}
              >
                <CloseIcon fontSize="small" />
              </Button>
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Typography 
              variant="h6" 
              component="h2" 
              sx={{ 
                fontWeight: 600,
                color: '#ffffff'
              }}
            >
              Document Assistant
            </Typography>
            
            {/* Premium Badge */}
            <Chip
              icon={<PremiumIcon sx={{ fontSize: 14 }} />}
              label="Premium"
              size="small"
              sx={{
                backgroundColor: '#dcfce7',
                color: '#166534',
                fontWeight: 500,
                fontSize: '0.65rem',
                height: 20,
                border: '1px solid #bbf7d0',
                '& .MuiChip-icon': { color: 'gold' }
              }}
            />
          </Box>
          
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '0.875rem'
            }}
          >
            Ask questions about your documents
          </Typography>
        </Box>
      )}

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ m: 2, mb: 0 }}
          action={
            <Button size="small" onClick={retryLastMessage}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Messages Container */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          p: { xs: 2, md: 3 },
          bgcolor: embedded ? 'transparent' : 'grey.50',
          scrollBehavior: 'smooth'
        }}
      >
        <DocumentMessageList 
          messages={messages} 
          onSuggestionClick={(suggestion) => sendMessage(suggestion)}
        />
      </Box>

      {/* Input Container */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 0,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          zIndex: 2
        }}
      >
        <Box sx={{ maxWidth: 900, mx: 'auto', width: '100%' }}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end' }}>
            <Box sx={{ flex: 1 }}>
              <ChatInput
                onSend={(question) => sendMessage(question)}
                disabled={isLoading}
                placeholder="Ask a question about your documents..."
                maxLength={2000}
              />
            </Box>
            
            {/* Clear Chat Button */}
            {messages.length > 0 && (
              <Tooltip title="Clear conversation" placement="top">
                <IconButton
                  onClick={clearMessages}
                  disabled={isLoading}
                  sx={{
                    width: 48,
                    height: 48,
                    color: 'text.secondary',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 3,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'error.lighter',
                      color: 'error.main',
                      borderColor: 'error.light'
                    }
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          
          {/* Footer info */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5, px: 1 }}>
            <Typography 
              variant="caption" 
              sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <ResearchIcon sx={{ fontSize: 14, opacity: 0.8 }} />
              AI Assistant uses your uploaded documents to provide answers
            </Typography>
            {expiryText && (
              <Typography 
                variant="caption" 
                sx={{ 
                  color: expiryText === 'Expired' ? 'error.main' : 'text.secondary',
                  fontWeight: 500
                }}
              >
                {expiryText}
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export const DocumentChatModal: React.FC<DocumentChatModalProps> = ({
  open,
  onClose,
  sourceJobId,
  documentTitle,
  expiresAt
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionComponent={Fade}
      PaperProps={{
        sx: {
          height: '85vh',
          maxHeight: '900px',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          overflow: 'hidden'
        }
      }}
    >
      <DocumentChatContent
        sourceJobId={sourceJobId}
        documentTitle={documentTitle}
        expiresAt={expiresAt}
        showHeader={true}
        onClose={onClose}
      />
    </Dialog>
  );
};

export default DocumentChatModal;
